import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { getSearchResults } from './ApiContainers/GlobalSearchHelpers';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import axios, { CancelTokenSource } from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getPlatformUserId } from '../../utils/utils';
import { USER_TYPE } from '../../utils/constants/user';
import { getCompanyInfoSearch } from './ApiContainers/GlobalSearchHelpers';
import { GLOBAL_SEARCH_GROUPS, GlobalSearchGroupKey } from '../MagicUpload/Utils/MagicUploadConstants';
import { setAddToWatchlistModal } from '../../store/investor/action';

export type GlobalSearchResult = {
    orgId: string | null;
    orgName: string;
    websiteUrl?: string;
    secondaryMessage?: string;
    cinNumber?: string;
    pan?: string;
    companyAccessType?: string;
    isIndividualPan?: boolean;
};

export type GlobalSearchResultGroups = {
    [key in GlobalSearchGroupKey]: GlobalSearchResult[];
};

const useSearchResultFetcher = () => {
    const [searchText, setSearchText] = useState('');
    const platformUserId = getPlatformUserId();
    const [searchResults, setSearchResults] = useState<GlobalSearchResultGroups | null>(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const userData = useAppSelector((state) => state.user.userData);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // A ref to keep track of the current cancel token source
    const cancelTokenRef: MutableRefObject<CancelTokenSource | null> = useRef(null);

    useEffect(() => {
        if (searchText.length === 0) {
            setSearchResults(null);
            setSearchLoading(false);
        }
        // We only start searching if text length > 3
        if (searchText.length <= 2) {
            setSearchLoading(false);
            return;
        }

        // Create a debouncer (1 second = 1000 ms)
        const debounceTimer = setTimeout(() => {
            // If a previous request is in-flight, cancel it
            if (cancelTokenRef.current) {
                cancelTokenRef.current.cancel('Request canceled due to new search query');
            }

            // Create a fresh CancelToken source
            const source = axios.CancelToken.source();
            cancelTokenRef.current = source;
            setSearchLoading(true);
            let payload: any = {
                parentOrgId: userData.investorOrgId,
                globalSearchText: searchText,
                cancelToken: source.token,
            };
            if (userData.accountType !== USER_TYPE.INVESTOR) payload.userId = platformUserId;

            // Fire the API call with the new token
            getSearchResults(payload)
                .then((res) => {
                    const groupedData: GlobalSearchResultGroups = {
                        ...res,
                        [GLOBAL_SEARCH_GROUPS.ALREADY_ADDED]: Array.isArray(res?.alreadyAdded) ? res.alreadyAdded : [],
                        [GLOBAL_SEARCH_GROUPS.ADD_NEW]: Array.isArray(res?.addNew) ? res.addNew : [],
                    };
                    setSearchResults(groupedData);
                })
                .catch((error) => {
                    if (axios.isCancel(error)) {
                        console.log('Request canceled:', error.message);
                    } else {
                        // Handle error from server or network
                        console.error('Error in search request:', error);
                    }
                    // Even on error, open dropdown with empty groups so UI can proceed.
                    setSearchResults({
                        [GLOBAL_SEARCH_GROUPS.ALREADY_ADDED]: [],
                        [GLOBAL_SEARCH_GROUPS.ADD_NEW]: [],
                    });
                })
                .finally(() => setSearchLoading(false));
        }, 300);

        // Cleanup function: clear the debounce timer and cancel any in-flight request
        return () => {
            clearTimeout(debounceTimer);
            if (cancelTokenRef.current) {
                cancelTokenRef.current.cancel('Cleanup: component unmount or effect re-run');
            }
            setSearchLoading(false);
        };
    }, [searchText]);

    // Handling User Navigation
    const navigateUser = (orgId: string, permissionType: string) => {
        if (!orgId) {
            toast.error('No organization found');
            return;
        }
        navigate(
            `/investor/app/portfolio/${orgId}?fetchType=all&page=1&type=${permissionType?.toLowerCase()}&tab=aicaReport&selectedTab=overview`,
            { replace: true },
        );
    };

    const navigateOrAddLead = (
        orgId: string | null,
        permissionType: string,
        result?: any,
        searchText?: string,
        companyInfo?: any,
        companyInfoLoading?: boolean,
        prefillLoadingFields?: { identifier?: boolean; website?: boolean; brand?: boolean },
        overwriteIdentifierFromCompanyInfo?: boolean,
        isAddUsingSearchText?: boolean,
    ) => {
        const identifierDisplay = result?.orgName || searchText || '';
        const identifierValue = result?.cinNumber || result?.pan || identifierDisplay;
        if (orgId) {
            navigateUser(orgId, permissionType);
            return;
        }
        dispatch(
            setAddToWatchlistModal({
                show: true,
                data: {
                    result,
                    searchText,
                    companyInfo,
                    companyInfoLoading,
                    prefillLoadingFields,
                    prefillIdentifierDisplay: identifierDisplay,
                    prefillIdentifierPayload: identifierValue,
                    overwriteIdentifierFromCompanyInfo,
                    isAddUsingSearchText: !!isAddUsingSearchText,
                    source: 'global_search',
                },
                onSuccess: null,
            }),
        );
    };

    const getTextForKey = (key: string) => {
        switch (key) {
            case GLOBAL_SEARCH_GROUPS.ALREADY_ADDED:
                return 'Already Added';
            case GLOBAL_SEARCH_GROUPS.ADD_NEW:
                return 'Add New';
            case 'WATCH':
                return 'Prospects';
            default:
                return key;
        }
    };

    const fetchCompanyInfoForResult = async (result: any, fallbackSearchText?: string) => {
        const payload: Record<string, string> = {};
        if (result?.cinNumber || result?.secondaryMessage) payload.cinNumber = result.cinNumber || result.secondaryMessage;
        if (result?.pan) payload.panNumber = result.pan;
        if (result?.orgName || fallbackSearchText) payload.companyName = result?.orgName || fallbackSearchText || '';

        if (Object.keys(payload).length === 0) return null;

        return await getCompanyInfoSearch(payload);
    };

    const handleAddNewFlow = async ({
        baseResult,
        accessType,
        searchTextValue,
        initialPrefillLoadingFields,
        overwriteIdentifierFromCompanyInfo,
        isAddUsingSearchText,
    }: {
        baseResult: GlobalSearchResult;
        accessType: string;
        searchTextValue: string;
        initialPrefillLoadingFields: { identifier?: boolean; website?: boolean; brand?: boolean };
        overwriteIdentifierFromCompanyInfo?: boolean;
        isAddUsingSearchText?: boolean;
    }) => {
        const resultWithGroup = { ...baseResult, companyAccessType: baseResult.companyAccessType || '' };

        navigateOrAddLead(
            null,
            accessType,
            resultWithGroup,
            searchTextValue,
            undefined,
            true,
            initialPrefillLoadingFields,
            overwriteIdentifierFromCompanyInfo,
            isAddUsingSearchText,
        );

        const companyInfo = await fetchCompanyInfoForResult(resultWithGroup, searchTextValue);
        navigateOrAddLead(
            null,
            accessType,
            { ...resultWithGroup, companyInfo },
            searchTextValue,
            companyInfo,
            false,
            { identifier: false, website: false, brand: false },
            overwriteIdentifierFromCompanyInfo,
            isAddUsingSearchText,
        );
    };

    return {
        searchText,
        setSearchText,
        searchResults,
        searchLoading,
        navigateUser,
        navigateOrAddLead,
        setSearchResults,
        getTextForKey,
        fetchCompanyInfoForResult,
        handleAddNewFlow,
    };
};

export default useSearchResultFetcher;
